import React, { useCallback, useContext, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { WarningAmberOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../services/providers/authProvider";
import { AccessGateContext } from "../../../../context/accessGateContext";
import ConfirmationModal from "../../../Shared/confirmationModal";
import accountService from "../../../../services/accountService";
import { MAX_ELIMINA_MOTIVATION_LEN } from "../../../../services/schemas/accountSchemas";
import { vendorMainColumnMaxWidthSx } from "../../../../config/vendorContentMaxWidth";
import { ACCENT, PRIMARY } from "../constants";

function EliminaProfilo() {
  const { logout, isImpersonated } = useAuth() || {};
  const navigate = useNavigate();
  const { allowVendorAreaAccess } = useContext(AccessGateContext);

  const [deleteMotivationOpen, setDeleteMotivationOpen] = useState(false);
  const [deleteMotivation, setDeleteMotivation] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  const openErrorModal = useCallback((message) => {
    setErrorModalMessage(message);
    setIsErrorModalOpen(true);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/home");
  }, [logout, navigate]);

  const openDeleteMotivation = useCallback(() => {
    if (isImpersonated) return;
    allowVendorAreaAccess();
    setDeleteMotivation("");
    setDeleteMotivationOpen(true);
  }, [allowVendorAreaAccess, isImpersonated]);

  const closeDeleteMotivation = useCallback(() => {
    setDeleteMotivationOpen(false);
    setDeleteMotivation("");
  }, []);

  const goToDeleteConfirm = useCallback(() => {
    setDeleteMotivationOpen(false);
    setDeleteConfirmOpen(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteMotivation("");
  }, []);

  const executeDeleteAccount = useCallback(async () => {
    setDeleteSubmitting(true);
    try {
      const payload = { role: "vendor" };
      const m = deleteMotivation.trim();
      if (m) payload.motivation = m;
      const response = await accountService.eliminaAccount(payload);
      if (response?.data?.statusCode === 202) {
        setDeleteConfirmOpen(false);
        setDeleteMotivation("");
        handleLogout();
        return;
      }
      openErrorModal(response?.data?.message ?? "Impossibile completare l'eliminazione.");
    } catch (error) {
      openErrorModal(error?.response?.data?.message ?? "Impossibile completare l'eliminazione.");
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteMotivation, handleLogout, openErrorModal]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 3.5 },
          borderRadius: "14px",
          border: `1px solid ${alpha(ACCENT, 0.25)}`,
          backgroundColor: alpha(ACCENT, 0.03),
          boxShadow: "0 8px 28px rgba(11, 61, 135, 0.07)",
          ...vendorMainColumnMaxWidthSx,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
            <WarningAmberOutlined sx={{ color: ACCENT, fontSize: 19, mt: 0.25, mr: 1, mb: 0.2 }} />
            <Typography
              sx={{
                fontFamily: "Rubik, sans-serif",
                fontWeight: 800,
                fontSize: "1rem",
                color: PRIMARY,
                textAlign: "center",
              }}
            >
              Eliminazione profilo
            </Typography>
            <WarningAmberOutlined sx={{ color: ACCENT, fontSize: 19, mt: 0.25, ml: 1, mb: 0.2 }} />
          </Box>

          <Typography
            sx={{
              fontFamily: "Rubik, sans-serif",
              fontSize: "0.875rem",
              color: "text.secondary",
              width: "100%",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            L'eliminazione del tuo profilo è permanente e comporterà la perdita di accesso all'area riservata e ai dati associati.
            <br />
            Il tuo catalogo e tutti i tuoi prodotti saranno eliminati e non più disponibili su wowcheprezzi.it.
          </Typography>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              type="button"
              onClick={openDeleteMotivation}
              sx={{
                mt: 3,
                fontFamily: "Rubik, sans-serif",
                color: ACCENT,
                backgroundColor: alpha(ACCENT, 0.03),
                fontWeight: 100,
                textTransform: "none",
                border: `1px solid ${ACCENT}`,
                borderRadius: 100,
                px: 2.5,
                "&:hover": { backgroundColor: `${ACCENT}`, color: "#ffffff" },
              }}
            >
              Elimina profilo
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={deleteMotivationOpen} onClose={closeDeleteMotivation} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontFamily: "Rubik",
            fontWeight: "bold",
            color: PRIMARY,
            textAlign: "center",
            width: "100%",
          }}
        >
          Perché vorresti eliminare il tuo account?
        </DialogTitle>
        <DialogContent sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
              mt: 1,
              mb: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{ fontFamily: "Rubik", fontWeight: 600, color: PRIMARY, fontSize: "0.875rem" }}
            >
              Motivazione
            </Typography>
            <Typography
              component="span"
              sx={{ fontFamily: "Rubik", color: "text.secondary", fontSize: "0.875rem", flexShrink: 0 }}
            >
              {deleteMotivation.length}/{MAX_ELIMINA_MOTIVATION_LEN}
            </Typography>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            minRows={4}
            value={deleteMotivation}
            onChange={(e) => setDeleteMotivation(e.target.value)}
            inputProps={{ maxLength: MAX_ELIMINA_MOTIVATION_LEN, style: { textAlign: "center" } }}
            sx={{ fontFamily: "Rubik", "& .MuiInputBase-input": { textAlign: "center" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, width: "100%", justifyContent: "center" }}>
          <Button
            onClick={closeDeleteMotivation}
            sx={{
              fontFamily: "Rubik",
              color: PRIMARY,
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={goToDeleteConfirm}
            variant="contained"
            sx={{
              fontFamily: "Rubik",
              backgroundColor: PRIMARY,
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Continua
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        title="Questa operazione è irreversibile"
        subtitle=""
        message="Eliminando il tuo account, tutti i dati associati saranno contrassegnati come eliminati in modo permanente."
        confirmColor={ACCENT}
        cancelText="Annulla"
        confirmText="Conferma"
        onConfirm={() => {
          if (!deleteSubmitting) executeDeleteAccount();
        }}
        centerText
      />

      <ConfirmationModal
        open={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="Errore!"
        subtitle=""
        message={errorModalMessage}
        onConfirm={() => setIsErrorModalOpen(false)}
        centerText
      />
    </>
  );
}

export default EliminaProfilo;
